import { LAYER_ORDER, DASH_OPTIONS } from "./studioUtils";

const WIDTHS = [0.15, 0.25, 0.35, 0.5, 0.7, 1.0, 1.4, 1.8];

export default function PropertiesPanel({ selected, drawing, elementsCount, onChange, locked }) {
  if (!selected.length) {
    return (
      <div className="p-3" data-testid="props-panel">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Drawing info</div>
        {drawing ? (
          <div className="space-y-1.5">
            {[["Name", drawing.name], ["Number", drawing.drawing_no], ["Revision", `Rev ${drawing.rev_label || drawing.revision}`],
              ["Status", drawing.status], ["Sheet", drawing.size || "A3"], ["Scale", drawing.scale || "—"],
              ["Elements", elementsCount]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-[11px]">
                <span className="text-[var(--c-muted)]">{k}</span>
                <span className="font-mono text-[var(--c-text)] text-right truncate ml-2">{String(v)}</span>
              </div>
            ))}
            <div className="text-[10px] text-[var(--c-muted)] border-t border-[var(--c-border)] pt-2 mt-2 leading-relaxed">
              Click an element to inspect it. Drag to move, use handles to resize/rotate.
            </div>
          </div>
        ) : <div className="text-[11px] text-[var(--c-muted)]">No drawing loaded.</div>}
      </div>
    );
  }

  const el = selected[0];
  const multi = selected.length > 1;
  const num = (v) => (typeof v === "number" ? Math.round(v * 10) / 10 : v);

  const field = (label, key, kind = "number", step = "0.1") => (
    <div className="flex items-center justify-between gap-2" key={key}>
      <label className="text-[10px] text-[var(--c-muted)]">{label}</label>
      <input data-testid={`prop-${key}`} type={kind} step={step} disabled={locked}
        key={`${el.uid}-${key}-${el[key]}`}
        className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] font-mono rounded-sm px-1.5 disabled:opacity-40"
        defaultValue={kind === "number" ? num(el[key] ?? 0) : el[key] ?? ""}
        onBlur={(e) => onChange([el.uid], { [key]: kind === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()} />
    </div>
  );

  return (
    <div className="p-3 space-y-2" data-testid="props-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
          {multi ? `${selected.length} elements` : `Element — ${el.kind}${el.dim ? " (dimension)" : ""}`}
        </div>
        {el.auto && <span className="text-[9px] font-mono text-[var(--c-amber)]">auto</span>}
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] text-[var(--c-muted)]">Layer</label>
        <select data-testid="prop-layer" disabled={locked}
          className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-1 disabled:opacity-40"
          value={el.layer} onChange={(e) => onChange(selected.map((s) => s.uid), { layer: e.target.value })}>
          {LAYER_ORDER.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] text-[var(--c-muted)]">Color</label>
        <input data-testid="prop-color" type="color" disabled={locked}
          className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm disabled:opacity-40"
          value={el.stroke || "#1a1a1a"} onChange={(e) => onChange(selected.map((s) => s.uid), { stroke: e.target.value })} />
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] text-[var(--c-muted)]">Line width</label>
        <select data-testid="prop-width" disabled={locked}
          className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-1 disabled:opacity-40"
          value={el.sw ?? ""} onChange={(e) => onChange(selected.map((s) => s.uid), { sw: parseFloat(e.target.value) || undefined })}>
          <option value="">layer</option>
          {WIDTHS.map((w) => <option key={w} value={w}>{w} mm</option>)}
        </select>
      </div>

      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] text-[var(--c-muted)]">Line style</label>
        <select data-testid="prop-dash" disabled={locked}
          className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-1 disabled:opacity-40"
          value={el.dash ? "dashed" : "solid"} onChange={(e) => onChange(selected.map((s) => s.uid), { dash: DASH_OPTIONS[e.target.value] })}>
          {Object.keys(DASH_OPTIONS).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>

      {el.kind === "text" && (
        <>
          <div className="flex items-center justify-between gap-2">
            <label className="text-[10px] text-[var(--c-muted)]">Text</label>
            <input data-testid="prop-text" disabled={locked} key={`${el.uid}-s-${el.s}`}
              className="w-24 h-6 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-1.5 disabled:opacity-40"
              defaultValue={el.s} onBlur={(e) => onChange([el.uid], { s: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && e.target.blur()} />
          </div>
          {field("Font size", "size", "number", "1")}
          {field("Angle °", "rotate", "number", "5")}
        </>
      )}
      {el.dim && (
        <div className="flex items-center justify-between gap-2">
          <label className="text-[10px] text-[var(--c-muted)]">Dim value</label>
          <span className="font-mono text-[11px] text-[var(--c-accent)]" data-testid="prop-dim-value">{num(el.dim_value)} mm</span>
        </div>
      )}
      {el.kind === "circle" && <>{field("Center X", "cx")}{field("Center Y", "cy")}{field("Radius", "r")}</>}
      {el.kind === "rect" && <>{field("X", "x")}{field("Y", "y")}{field("Width", "w")}{field("Height", "h")}</>}
      {el.kind === "text" && <>{field("X", "x")}{field("Y", "y")}</>}
      {el.kind === "line" && <>{field("X1", "x1")}{field("Y1", "y1")}{field("X2", "x2")}{field("Y2", "y2")}</>}
    </div>
  );
}
